import { ESPLoader } from './firmware-js/ESPLoader';
import { Transport } from './firmware-js/webserial';

const VERSION_LIST_URL = 'https://embedded.plode.org/version-list';
const API_BASE = 'https://embedded.plode.org/v2';

const SERIAL_PORT_FILTERS = [
    { usbVendorId: 0x1a86, usbProductId: 0x7523 },
    { usbVendorId: 0x303a, usbProductId: 0x1001 },
    { usbVendorId: 0x1a86, usbProductId: 0x8040 },
    { usbVendorId: 0xb1b0, usbProductId: 0x8055 },
    { usbVendorId: 0xb1b0, usbProductId: 0x8056 },
    { usbVendorId: 0xb1b0, usbProductId: 0x8057 },
    { usbVendorId: 0xb1b0, usbProductId: 0x8058 },
    { usbVendorId: 0xb1b0, usbProductId: 0x8059 },
];

const WHO_CODE_TO_VERSION_KEY = {
    PC: 'playcomputer',
    ZG: 'zing',
    CW: 'crawlee',
    KW: 'klaw',
    RB: 'roboki',
    TK: 'toki',
    PK: 'poki',
    MK: 'moki',
};

const PEECEE_MIN_FIRMWARE_BY_BRANCH = {
    '1.0': '1.0.12',
    '1.1': '1.1.12',
};

const DEVICE_CODE_TO_PREFIX = {
    playcomputer: 'PC',
    zing: 'ZG',
    crawlee: 'CW',
    roboki: 'RB',
    klaw: 'KW',
    toki: 'TK',
    poki: 'PK',
    moki: 'MK',
};

const compareVersions = (v1, v2) => {
    const a = String(v1 || '').split('.')
        .map(n => parseInt(n, 10) || 0);
    const b = String(v2 || '').split('.')
        .map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
        const x = a[i] || 0;
        const y = b[i] || 0;
        if (x !== y) return x > y ? 1 : -1;
    }
    return 0;
};

const getBranch = version => String(version || '').split('.')
    .slice(0, 2)
    .join('.');

const fetchVersionList = async() => {
    const cached = sessionStorage.getItem('versionData');
    if (cached) {
        try {
            return JSON.parse(cached);
        } catch (e) {
            sessionStorage.removeItem('versionData');
        }
    }
    const res = await fetch(VERSION_LIST_URL);
    if (!res.ok) throw new Error(`Version list request failed: ${res.status}`);
    const data = await res.json();
    sessionStorage.setItem('versionData', JSON.stringify(data));
    return data;
};

const checkFirmwareVersion = async(whoCode, currentVersion) => {
    const deviceName = WHO_CODE_TO_VERSION_KEY[whoCode];
    if (!deviceName || !currentVersion) return { updateAvailable: false };

    let versionData;
    try {
        versionData = await fetchVersionList();
    } catch (e) {
        console.error('[firmware] could not fetch version list:', e);
        return { updateAvailable: false };
    }

    const branch = getBranch(currentVersion);
    const entry = versionData[deviceName] && versionData[deviceName][branch];
    if (!entry) return { updateAvailable: false };

    const minFirmware = whoCode === 'PC' && currentVersion[0] === '1' ?
        PEECEE_MIN_FIRMWARE_BY_BRANCH[branch] : null;
    const mandatory = !!minFirmware && compareVersions(currentVersion, minFirmware) < 0;

    return {
        updateAvailable: compareVersions(entry.version, currentVersion) > 0,
        mandatory,
        currentVersion,
        latestVersion: entry.version,
        firmwarePath: entry.path,
        deviceName,
        prefix: DEVICE_CODE_TO_PREFIX[deviceName] || '',
        whoCode,
    };
};

const normalizeFirmwarePath = rawPath => {
    if (!rawPath) return null;
    const idx = rawPath.indexOf('/bins/');
    if (idx !== -1) return rawPath.substring(idx);
    return `/bins/production/${rawPath}`;
};

const fetchFirmwareFiles = async firmwareCheck => {
    const basePath = normalizeFirmwarePath(firmwareCheck.firmwarePath);
    if (!basePath) throw new Error('Invalid firmware path');

    const changelogRes = await fetch(`${API_BASE}${basePath}/changelog.json`);
    if (!changelogRes.ok) throw new Error('Failed to fetch firmware changelog');
    const changelog = await changelogRes.json();
    const versions = Object.keys(changelog);
    if (versions.length === 0) throw new Error('No firmware versions found in changelog');

    let targetVersion = versions.find(v => v === firmwareCheck.latestVersion) ||
        versions.find(v => v === `${firmwareCheck.prefix}${firmwareCheck.latestVersion}`);
    if (!targetVersion) {
        const numeric = v => v.replace(/^[a-zA-Z_]+/, '');
        targetVersion = versions.sort((a, b) => compareVersions(numeric(b), numeric(a)))[0];
    }

    const entry = changelog[targetVersion];
    const locationMap = (entry && (entry.location || entry.locations)) || {};
    if (Object.keys(locationMap).length === 0) throw new Error('No firmware file locations found');

    const folderPath = `${basePath}/${targetVersion}`;
    const filesRes = await fetch(`${API_BASE}/filenames`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: folderPath }),
    });
    if (!filesRes.ok) throw new Error(`Failed to fetch firmware filenames: ${filesRes.statusText}`);
    const filesList = (await filesRes.json()).files || [];

    const files = [];
    Object.keys(locationMap).forEach(nameKey => {
        const fileName = filesList.find(f => f.includes(nameKey));
        if (fileName) {
            files.push({ fileName, address: parseInt(locationMap[nameKey], 10), data: null });
        }
    });
    if (files.length === 0) throw new Error('No matching firmware files found');

    for (const file of files) {
        const fileRes = await fetch(`${API_BASE}${folderPath}/${file.fileName}`);
        if (!fileRes.ok) throw new Error(`Download failed for ${file.fileName}: ${fileRes.statusText}`);
        const blob = await fileRes.blob();
        file.data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = e => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsBinaryString(blob);
        });
    }
    return files;
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

class BootloaderConnectError extends Error {}
class PortSelectionError extends Error {}

const flashFirmware = async(firmwareCheck, onStatus) => {
    onStatus('Please select your device to start the firmware update...');
    let device;
    try {
        const paired = await navigator.serial.getPorts({ filters: SERIAL_PORT_FILTERS });
        device = paired[0] ||
            await navigator.serial.requestPort({ filters: SERIAL_PORT_FILTERS });
    } catch (e) {
        throw new PortSelectionError('Device selection was cancelled.');
    }

    if (device.readable || device.writable) {
        try {
            await device.close();
        } catch (e) {
            console.warn('[firmware] close warning:', e);
        }
        await sleep(300);
    }
    await sleep(200);

    const transport = new Transport(device);
    const esploader = new ESPLoader(transport, 460800);

    onStatus('Connecting to device...');
    try {
        await esploader.main_fn();
    } catch (e) {
        console.error('[firmware] ESPLoader sync failed:', e);
        try {
            await transport.disconnect();
        } catch (err) { /* ignore */ }
        throw new BootloaderConnectError('Could not connect to bootloader.');
    }

    onStatus('Downloading firmware...');
    const files = await fetchFirmwareFiles(firmwareCheck);

    onStatus('Erasing chip (Do not disconnect)...');
    await esploader.erase_flash();

    onStatus('Writing firmware...', 0);
    await esploader.write_flash({
        fileArray: files,
        flash_size: 'keep',
        reportProgress: (fileIndex, written, total) =>
            onStatus('Writing firmware...', Math.round((written / total) * 100)),
    });

    await transport.disconnect();

    onStatus('Resetting device...');
    if (!device.readable) {
        await transport.connect({ baud: 115200 });
    }
    await transport.setDTR(false);
    await transport.setRTS(true);
    await sleep(100);
    await transport.setRTS(false);
    await transport.setDTR(false);
    await sleep(1000);
    await transport.disconnect();
};

export {
    BootloaderConnectError,
    PortSelectionError,
    checkFirmwareVersion,
    compareVersions,
    flashFirmware,
};
